/**
 * Resume deep-link handling (IX-3738).
 *
 * A ChatGPT-app (Rose MCP) card links to the brand's site with
 * `?rose_resume=<session_id>`. On load the widget exchanges that id for the
 * session's prior turns, adopts the id as its own chat session (so LangGraph
 * memory continues server-side), seeds the transcript into chat storage, and
 * opens expanded. The session id is the capability: unguessable and expiring
 * with the backend session TTL — an invalid or expired id falls back to a
 * normal fresh-session boot.
 */

import { RESUME_GREETING_MARKER } from '../config/constants';
import { logger } from '../utils/core/logger';
import {
    ChatStorageService,
    type ConversationPair,
    createAssistantMessage,
    createConversationPair,
    createUserMessage,
    type UserMessage,
} from './chatStorage';
import type { InboundXService } from './inboundx';

const RESUME_PARAM = 'rose_resume';
// The loader (rose-loader.js) stashes the param here under the same key the instant
// it runs, so a client-side locale/consent redirect that drops the query string
// (e.g. payfit.com/ → payfit.com/fr/) can't lose the deep-link. See loader.ts.
const RESUME_STASH_KEY = 'rose_resume';

/**
 * Read a resumable session id for this load: the `?rose_resume=` URL param, or —
 * when a redirect has already stripped it — the loader's sessionStorage stash.
 */
export function getResumeSessionIdFromUrl(): string | null {
    try {
        const fromUrl = new URLSearchParams(window.location.search).get(RESUME_PARAM);
        let stashed: string | null = null;
        try {
            stashed = sessionStorage.getItem(RESUME_STASH_KEY);
            // One-shot: consume the stash on read. If this load never reaches
            // hydration (widget bundle fails, ConfigProvider throws first), the
            // stash must not survive to replay a stale resume on a later same-tab
            // navigation. sessionStorage otherwise lives until the tab closes.
            if (stashed) {
                sessionStorage.removeItem(RESUME_STASH_KEY);
            }
        } catch {
            // sessionStorage blocked — URL param is the only source.
        }
        const param = fromUrl ?? stashed;
        if (!param) {
            return null;
        }
        // Only MCP-originated sessions are resumable (the backend enforces this
        // too); reject anything else before making a network call.
        return param.startsWith('rose_mcp_') ? param : null;
    } catch {
        return null;
    }
}

function stripResumeParamFromUrl(): void {
    try {
        sessionStorage.removeItem(RESUME_STASH_KEY);
    } catch {
        // Best effort — the stash is one-shot anyway (cleared on next fresh boot).
    }
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(RESUME_PARAM)) {
            return;
        }
        url.searchParams.delete(RESUME_PARAM);
        window.history.replaceState(window.history.state, '', url.toString());
    } catch {
        // Best effort — a refresh would just re-run the resume, which is harmless.
    }
}

function buildConversationPairs(
    messages: { role: 'user' | 'assistant'; content: string }[]
): ConversationPair[] {
    const pairs: ConversationPair[] = [];
    // Real turn timestamps aren't returned; synthetic ones keep ordering stable.
    const base = Date.now() - messages.length * 1000;
    let pendingQuestion: UserMessage | null = null;

    messages.forEach((message, index) => {
        const timestamp = base + index * 1000;
        const id = `resume_${timestamp}_${index}`;
        if (message.role === 'user') {
            if (pendingQuestion) {
                pairs.push(createConversationPair(pendingQuestion));
            }
            pendingQuestion = createUserMessage(id, message.content, timestamp);
        } else {
            // No preceding user turn (e.g. the brand's greeting after the MCP connect
            // opener was dropped server-side, IX-3852): pair the assistant message with
            // a hidden-marker question so no empty user bubble renders above it.
            const question =
                pendingQuestion ?? createUserMessage(`${id}_q`, RESUME_GREETING_MARKER, timestamp);
            pairs.push(
                createConversationPair(
                    question,
                    createAssistantMessage(id, message.content, { timestamp })
                )
            );
            pendingQuestion = null;
        }
    });
    if (pendingQuestion) {
        pairs.push(createConversationPair(pendingQuestion));
    }
    return pairs;
}

/**
 * Exchange a resume session id for its history and seed the widget with it.
 *
 * On success the service adopts the session id (memory continuity) and the
 * transcript is written to chat storage with `isExpanded: true`, so the widget
 * auto-opens showing the prior turns. Returns false (after cleaning the URL)
 * when the session is expired, foreign, or the fetch fails — callers should
 * then boot normally.
 */
export async function hydrateResumedConversation(
    service: InboundXService,
    siteName: string,
    sessionId: string
): Promise<boolean> {
    try {
        const messages = await service.fetchResumeHistory(sessionId);
        if (messages.length === 0) {
            stripResumeParamFromUrl();
            return false;
        }

        service.adoptSessionId(sessionId);
        ChatStorageService.saveChatState(siteName, {
            conversationPairs: buildConversationPairs(messages),
            isExpanded: true,
            isExplicitlyMinimized: false,
            sessionId,
            siteName,
        });
        stripResumeParamFromUrl();
        logger.info('Resume: seeded conversation from deep-link', {
            siteName,
            turns: messages.length,
        });
        return true;
    } catch (error) {
        logger.warn('Resume: failed to hydrate conversation, booting fresh', error);
        stripResumeParamFromUrl();
        return false;
    }
}
